import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LoggerService } from './logger.service';
import { SettingsService } from './settings.service';
import { Group } from 'app/original-models/group.model';
import { Observable, BehaviorSubject, Subscription, of } from 'rxjs';
import { AuthenticationService } from './authentication.service';
import { map, catchError } from 'rxjs/operators';
import { IntercomService } from 'app/services/intercom/intercom.service';
import { IntercomSettings } from 'app/original-models/intercom-settings.model';
import {StorageService} from './storage.service';

@Injectable({ providedIn: 'root' })
export class GroupService {
    apiUrl: string;
    private _groupsSubject = new BehaviorSubject<Group[]>(null);
    private _currentGroupSubject = new BehaviorSubject<Group>(null);
    private _groupsSubscription: Subscription;
    private _initialized = false;
    private _currentGroupChanged = new BehaviorSubject<Group>(null);
    currentGroupChanged$ = this._currentGroupChanged.asObservable();

    constructor(
        private http: HttpClient,
        private settings: SettingsService,
        private authService: AuthenticationService,
        private intercom: IntercomService,
        private storage: StorageService,
        private logger: LoggerService
    ) {
        this.apiUrl = this.settings.apiUrl;

        this.authService.afterLogin$.subscribe(() => {
            this.updateGroups(null);
            this._currentGroupSubject.next(null);
            this._currentGroupChanged.next(null);
            this._groupsSubscription = null;
            this._initialized = false;
        });

        this.authService.beforeLogout$.subscribe(() => {
            this.updateGroups(null);
            this._currentGroupSubject.next(null);
            this._currentGroupChanged.next(null);
            this._groupsSubscription = null;
            this._initialized = false;
        });
    }

    // getCachedCurrentGroup(id: number): Observable<Group> {
    //     let groups = this._groupsSubject.value;
    //     let group;
    //     if(groups) {
    //         group = this.getSubgroup(groups, id, group);
    //         this._currentGroupSubject = new BehaviorSubject<Group>(null);
    //         this._currentGroupSubject.next(group);
    //         return this._currentGroupSubject.asObservable();
    //     }
    //     if(!group) {
    //         return this.getCurrentGroup(id);
    //     }

    // }

    getIntercomSettings(
        id: number,
        userName: string
    ): Observable<IntercomSettings> {
        return this.http.get(`${this.apiUrl}/corporate-groups/${id}/`)
        .pipe(
            map((res: any) => {
                if (res) {
                    return new IntercomSettings().setFromApiModelFromGroup(
                        res,
                        userName
                    );
                }
            }),
            catchError((error) => {
                this.logger.error(error);
                return of(null);
            })
        );
    }
    getCurrentGroup(id: number, refresh?: boolean): Observable<Group> {
        if (refresh || !this._initialized) {
            this._initialized = true;
            if (
                this._currentGroupSubject &&
                this._currentGroupSubject.value !== null &&
                this._currentGroupSubject.value.id !== id
            ) {
                this._currentGroupSubject = new BehaviorSubject<Group>(null);
            }
            let changed =
                !this._currentGroupSubject?.value ||
                this._currentGroupSubject.value.id !== id;

            return this.fetchGroup(id).pipe(
                map((value) => {
                    this._currentGroupSubject.next(value);
                    if (changed) {
                        this._currentGroupChanged.next(value);
                    }
                    return value;
                })
            );
        } else {
            return this._currentGroupSubject.asObservable();
        }
    }

    private fetchGroup(id: number): Observable<Group> {
        return this.http.get(`${this.apiUrl}/corporate-groups/${id}/`).pipe(
            map((res: any) => {
                let group = new Group().setFromApiModel(res);
                this.storage.setStorageItem('currency', group.currency);
                return group
            }),
            catchError((error) => {
                this.logger.error(error);
                return of(null);
            })
        );
    }

    getAllGroups(withoutUpdate?: boolean): Observable<Group[]> {
        return this.http.get(`${this.apiUrl}/corporate-groups/`).pipe(
            map((data: any[]) => {
                let res = [];
                res = data.map((value) => {
                    return new Group().setFromApiModel(value);
                });
                if (res.length) {
                    if (!withoutUpdate) {
                        this.updateGroups(res);
                    }
                    return res;
                } else {
                    return [];
                }
            })
        );
    }

    getGroups(): Observable<Group[]> {
        this._groupsSubject = new BehaviorSubject<Group[]>(null);
        this.fetchGroups();
        return this._groupsSubject.asObservable();
    }

    private updateGroups(groups: Group[]) {
        this._groupsSubject.next(groups);
        this._groupsSubscription = null;
    }

    private fetchGroups(): void {
        this._groupsSubscription = this.http
            .get(`${this.apiUrl}/corporate-groups/`)
            .subscribe(
                (res: any[]) => {
                    let items = [];
                    items = res.map((value) => {
                        return new Group().setFromApiModel(value);
                    });
                    this.updateGroups(items);
                },
                (error) => {
                    this.logger.error(error);
                    this.updateGroups(null);
                }
            );
    }

    private updateGroupsWithUpdatedItem(group: Group, isDeleted = false) {
        let groups = this._groupsSubject.value;
        let parentGroup = null;
        parentGroup = this.getSubgroup(groups, group.parent, parentGroup);
        if (parentGroup) {
            let existingItemIndex = parentGroup.subGroups.findIndex(
                (g) => g.id === group.id
            );
            if (isDeleted) {
                if (existingItemIndex >= 0) {
                    parentGroup.subGroups.splice(existingItemIndex, 1);
                }
            } else if (existingItemIndex >= 0) {
                parentGroup.subGroups[existingItemIndex] = group;
            } else {
                parentGroup.subGroups.push(group);
            }
        } else if (!groups || groups.length == 0) {
            groups = [];
            groups.push(group);
        }
        this.updateGroups(groups);
    }

    private getSubgroup(groups: Group[], parentId: number, result: Group) {
        if (!result && groups && groups.length > 0) {
            result = groups.find((g) => g.id === parentId);
            if (!result) {
                groups.forEach((subgroup) => {
                    result = this.getSubgroup(
                        subgroup.subGroups,
                        parentId,
                        result
                    );
                });
            }
        }
        return result;
    }

    saveGroup(group: Group, type?: string): Observable<Group> {
        if (group.id) {
            //update info
            return this.http
                .patch(
                    `${this.apiUrl}/corporate-groups/${group.id}/`,
                    group.getApiModel()
                )
                .pipe(
                    map((data) => {
                        let item = new Group().setFromApiModel(data);
                        this.updateGroupsWithUpdatedItem(item);
                        return item;
                    })
                );
        } else {
            //create new
            return this.http
                .post(`${this.apiUrl}/corporate-groups/`, group.getApiModel())
                .pipe(
                    map((data) => {
                        let item = new Group().setFromApiModel(data);
                        if (type) {
                            this.intercom.trackEvent(`${type}-created`);
                        }
                        this.updateGroupsWithUpdatedItem(item);
                        return item;
                    })
                );
        }
    }

    updateGroupParent(item: any): Observable<Group> {
        if (item.id) {
            const formData = new FormData();
            formData.append('id', String(item.id));
            if (item.parent) {
                formData.append('parent', String(item.parent));
            } else {
                formData.append('parent', null);
            }
            if (item.order) {
                formData.append('order', String(item.order));
            }
            return this.http
                .patch(`${this.apiUrl}/corporate-groups/${item.id}/`, formData)
                .pipe(
                    map((data) => {
                        let item = new Group().setFromApiModel(data);
                        this.updateGroupsWithUpdatedItem(item);
                        return item;
                    })
                );
        }
    }

    updateGroupOrder(item: any): Observable<Group> {
        if (item.id) {
            const formData = new FormData();
            formData.append('id', String(item.id));
            formData.append('order', String(item.order));
            return this.http
                .patch(`${this.apiUrl}/corporate-groups/${item.id}/`, formData)
                .pipe(
                    map((data) => {
                        let item = new Group().setFromApiModel(data);
                        this.updateGroupsWithUpdatedItem(item);
                        return item;
                    })
                );
        }
    }

    deleteGroup(group: Group): any {
        if (group) {
            return this.http.delete(`${this.apiUrl}/corporate-groups/${group.id}/`).pipe(
                map((data: any) => {
                    this.updateGroupsWithUpdatedItem(group, true);
                    return data;
                })
            );
        }
    }

    getForecast(id: number): Observable<any> {
        return this.http.get(`${this.apiUrl}/corporate-groups/${id}/forecast/`).pipe(
            map((data) => {
                // let item =  new Group().setFromApiModel(data);
                // this.updateGroupsWithUpdatedItem(item);
                return data;
            })
        );
    }
}
